import { Table, TableHead, TableTitle, Text, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Divider, FlatList, SectionList } from 'native-base';
import { useState, useMemo, useCallback } from 'react';

import { maxHeightMobile, maxHeight, rowHeight } from '../../constants';
import { useFinancialSummaryOrdenation } from '../../hooks/financialSummaryOrdenation';

export const FinancialTable = ({
  isLoading,
  allData = [],
  isMobile,
  columnWidths,
  renderItem,
  headers,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'defaultKey', direction: 'asc' });
  const sortedData = useFinancialSummaryOrdenation(allData, sortConfig);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const groupedDataByDate = useMemo(() => {
    const groupedData: Record<string, { relatedDate: string; data: any[] }> = allData.reduce(
      (acc, item) => {
        const date = (item as { relatedDateNormalized: string }).relatedDateNormalized;
        if (!acc[date]) {
          acc[date] = { relatedDate: date, data: [] };
        }
        acc[date].data.push(item);
        return acc;
      },
      {}
    );

    const today = Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy');
    const yesterday = Utils.Mask.DateFormat(new Date(Date.now() - 86400000), 'dd/MM/yyyy');

    return Object.values(groupedData).map((group) => ({
      ...group,
      relatedDate:
        group.relatedDate === today
          ? 'Hoje'
          : group.relatedDate === yesterday
          ? 'Ontem'
          : group.relatedDate,
    }));
  }, [allData]);

  return (
    <Table
      testId="table"
      isLoading={isLoading}
      rows={headers.length}
      cols={headers.length}
      rowHeight={rowHeight}>
      {!isMobile && (
        <TableHead testId="header">
          {headers.map(({ key, label, align }) => {
            const isSorted = sortConfig.key === key;
            const isAscending = sortConfig.direction === 'asc';
            return (
              <TableTitle
                key={key}
                textAlign={align}
                width={columnWidths[key]}
                testId={`header-${key}`}
                hasOrder
                orderByIsASC={isAscending}
                orderByIsSelected={isSorted}
                onPress={() => handleSort(key)}>
                {label}
              </TableTitle>
            );
          })}
        </TableHead>
      )}
      {!isMobile ? (
        <FlatList
          data={sortedData}
          windowSize={10}
          maxToRenderPerBatch={4}
          updateCellsBatchingPeriod={5}
          keyExtractor={(item, index) => index.toString()}
          contentContainerStyle={{ height: maxHeight }}
          showsVerticalScrollIndicator={false}
          renderItem={renderItem}
        />
      ) : (
        <SectionList
          sections={groupedDataByDate}
          keyExtractor={(item, index) => index.toString()}
          renderItem={renderItem}
          renderSectionHeader={({ section: { relatedDate } }) => (
            <VStack alignItems="flex-start" my={Spacing['inline.xxxs']}>
              <Text
                variant="regular"
                fontSize={Typography['fontsize.md']}
                mb={Spacing['inline.xxxs']}>
                {relatedDate}
              </Text>
              <Divider bg="neutral.light" />
            </VStack>
          )}
          contentContainerStyle={{ height: maxHeightMobile }}
          showsVerticalScrollIndicator={false}
        />
      )}
    </Table>
  );
};
